import { useEffect, useRef } from 'react';
import type { PlannerLegHeat } from './planner_models.js';
import { formatWeekdayDayMonth, parseDateOnly } from '@voll-intelligence/dates';
import type { PlannerHeatView } from './heat_view.js';
import type { FlightPlannerCopy } from './planner_copy.js';

/*
 * A recomendação da saia do planner binário, num módulo puro: o dia mais barato
 * da perna em escolha e, abaixo dele, a economia estimada de trocar a data
 * escolhida por ele (#5246).
 *
 * A conta mora no CLIENTE porque o servidor não conhece a seleção: escolher a ida
 * lê o heat com `returnDate = ida` (o artifício da #2106 para trazer a 2ª perna)
 * e escolher a volta não lê nada, de propósito. O preço por dia viaja no bloco só
 * para isto; a célula continua sendo cor.
 *
 * Tudo aqui é uma varredura linear dos dias de UMA perna (~90), uma vez por render
 * da saia: nada entra no caminho por célula da grade. As funções são puras; o único
 * efeito é o `useRememberedPrices`, que segura a memória curta dos preços.
 */

/** Abaixo disto (da data escolhida) a diferença é ruído de tarifa, não motivo para
 *  mudar de dia: nenhum número aparece (CT-4). */
const SAVINGS_FLOOR_RATIO = 0.05;

type LegDays = PlannerLegHeat['days'];

/** A recomendação da saia: a frase do dia mais barato (ou a de que a escolha já
 *  é ele) e, quando há, a 2ª linha — a economia, ou "Aguardando a data..."
 *  enquanto a seleção não fecha. `inline` é a mesma 2ª linha depois do lastro,
 *  na linha só da folha ("144 datas analisadas · voe neste dia e economize..."). */
export type SkirtBest = {
  readonly day: string;
  readonly follow?: { readonly text: string; readonly inline: string; readonly awaiting: boolean };
};

/**
 * O dia mais barato da VOLTA entre os que ainda são volta: na ida escolhida ou
 * depois. Com janela, o servidor lê a volta no MESMO intervalo da ida, então o
 * `cheapest.returnDate` dele pode cair antes da ida — e a saia recomendaria um dia
 * que a grade nem deixa escolher. Sem preços (bloco anterior a eles), vale o do
 * servidor quando ele já é volta possível.
 */
export function cheapestReturnFrom(heat: PlannerHeatView, out: string): string | undefined {
  let best: { date: string; price: number } | undefined;
  for (const day of heat.return?.days ?? []) {
    if (day.date < out || day.price === undefined) continue;
    if (!best || day.price < best.price) best = { date: day.date, price: day.price };
  }
  if (best) return best.date;
  const fromServer = heat.cheapest?.returnDate;
  return fromServer && fromServer >= out ? fromServer : undefined;
}

/** A economia, em unidades inteiras da moeda, de trocar `chosen` por `best` numa
 *  perna. `undefined` = nada a dizer: mesma data, um dos dois sem preço, ou
 *  diferença abaixo do piso (nunca "economize R$ 0"). */
export function legSavings(days: LegDays, chosen: string, best: string): number | undefined {
  if (chosen === best) return undefined;
  const chosenPrice = days.find((day) => day.date === chosen)?.price;
  const bestPrice = days.find((day) => day.date === best)?.price;
  if (chosenPrice === undefined || bestPrice === undefined) return undefined;
  const amount = Math.round(chosenPrice - bestPrice);
  return amount >= 1 && amount >= chosenPrice * SAVINGS_FLOOR_RATIO ? amount : undefined;
}

/**
 * A memória CURTA dos preços da ida: o heat chega por janela de 3 meses e o host
 * SUBSTITUI o bloco a cada janela (rolar a folha, as setas do popover), então a
 * data escolhida pode sair do bloco em vista — e sem o preço dela a economia some
 * (o celular rola para a frente, a ida fica para trás). A memória guarda o que já
 * foi lido para a MESMA rota, moeda e grupo (o preço é o total do grupo) e vale só
 * para a conta da economia; a grade continua lendo o bloco.
 *
 * Vive pouco de propósito: só enquanto o planner está montado, sem storage, e cada
 * dia expira em `PRICE_MEMO_TTL_MS` — preço velho vira economia mentirosa.
 */
export const PRICE_MEMO_TTL_MS = 5 * 60_000;

export type PriceMemo = {
  readonly scope: string;
  readonly days: ReadonlyMap<
    string,
    { readonly tier: number; readonly price: number; readonly readAt: number }
  >;
};

export const EMPTY_PRICE_MEMO: PriceMemo = { scope: '', days: new Map() };

function memoScope(heat: PlannerHeatView, passengers: number): string {
  return `${heat.route.origin}-${heat.route.destination}|${heat.currency ?? ''}|${String(passengers)}`;
}

/** Junta os preços da ida de `heat` à memória: escopo novo zera, dia expirado sai,
 *  a leitura nova vence a velha. */
export function absorbOutboundPrices(
  memo: PriceMemo,
  heat: PlannerHeatView,
  passengers: number,
  now: number,
): PriceMemo {
  const scope = memoScope(heat, passengers);
  const days = new Map(scope === memo.scope ? memo.days : []);
  for (const [date, entry] of days) if (now - entry.readAt > PRICE_MEMO_TTL_MS) days.delete(date);
  for (const day of heat.outbound?.days ?? []) {
    if (day.price !== undefined) {
      days.set(day.date, { tier: day.tier, price: day.price, readAt: now });
    }
  }
  return { scope, days };
}

/** O heat da conta da economia: os dias da ida do bloco, completados com os da
 *  memória que o bloco não traz (outra janela, ainda no prazo). */
export function withRememberedPrices(
  heat: PlannerHeatView,
  memo: PriceMemo,
  passengers: number,
  now: number,
): PlannerHeatView {
  if (!heat.outbound || memo.scope !== memoScope(heat, passengers)) return heat;
  const inBlock = new Set(heat.outbound.days.map((day) => day.date));
  const remembered = [...memo.days]
    .filter(([date, entry]) => !inBlock.has(date) && now - entry.readAt <= PRICE_MEMO_TTL_MS)
    .map(([date, { tier, price }]) => ({ date, tier, price }));
  if (remembered.length === 0) return heat;
  return { ...heat, outbound: { ...heat.outbound, days: [...heat.outbound.days, ...remembered] } };
}

/**
 * O heat da conta da economia com a memória curta dos preços da ida. A memória é
 * do planner montado: desmontar esquece. O render só LÊ a memória; o bloco entra
 * nela depois do commit, para que um render descartado nunca deixe preço de um
 * bloco que não chegou à tela. Ler a memória anterior ao bloco dá a mesma conta:
 * os dias do bloco já estão nele, e escopo novo não casa com a memória velha.
 */
export function useRememberedPrices(
  heat: PlannerHeatView,
  travelers: number,
  requested: number | undefined,
): PlannerHeatView {
  // O tamanho do grupo que o heat cotou: o QUEM quando há viajantes, senão o
  // `passengers` do pedido, senão 1 (o padrão do servidor). Sem roster, é o
  // `passengers` que distingue um grupo de outro.
  const passengers = travelers || requested || 1;
  // Ref e não estado: o bloco em cena já está na conta deste render, e a memória
  // só precisa dele no render SEGUINTE. Com estado, cada bloco novo custava um
  // render a mais do planner inteiro (#5693).
  const memo = useRef<PriceMemo>(EMPTY_PRICE_MEMO);
  useEffect(() => {
    memo.current = absorbOutboundPrices(memo.current, heat, passengers, Date.now());
  }, [heat, passengers]);
  // A cada render, sem memo: o prazo vence com o relógio, não com o bloco — trocar
  // só a data escolhida depois do prazo não pode reaproveitar preço vencido. A conta
  // é a mesma varredura de uma perna que a saia já faz por render.
  // eslint-disable-next-line react/refs, react/purity -- the remembered prices skip a planner render per heat block (#5693); moving them to state or a timer is #6816
  return withRememberedPrices(heat, memo.current, passengers, Date.now());
}

/**
 * A recomendação da saia binária. A frase de cima nomeia a perna em escolha: a
 * volta enquanto se escolhe a volta, a ida no resto. A economia é DESSA perna,
 * contra a data escolhida nela, e só com a seleção fechada (ida no somente ida;
 * ida e volta no ida e volta) — antes disso não há "trocar de dia", e a 2ª linha
 * diz que espera a data (CT-6). O preço é o total do grupo, então a economia
 * também é.
 */
export function skirtBest(p: {
  heat: PlannerHeatView;
  /** A fatia do planner binário que decide a frase (o `useBinaryPlanner`). */
  binary: {
    out: string | undefined;
    ret: string | undefined;
    oneWayTab: boolean;
    pickingReturn: boolean;
    showCalMeta: boolean;
  };
  copy: FlightPlannerCopy;
  locale: string;
}): SkirtBest | undefined {
  const { heat, copy, locale } = p;
  const { out, ret, oneWayTab, pickingReturn, showCalMeta } = p.binary;
  const returnHint = pickingReturn && out ? cheapestReturnFrom(heat, out) : undefined;
  const bestDate = returnHint ?? heat.cheapest?.outboundDate;
  if (!showCalMeta || !bestDate) return undefined;
  const best = parseDateOnly(bestDate);
  const date = best ? formatWeekdayDayMonth(best, locale, new Date()) : '';
  const day = returnHint ? copy.bestDayRet({ date }) : copy.bestDayOut({ date });
  if (!out || (!oneWayTab && !ret)) {
    return { day, follow: { text: copy.awaitingDate, inline: copy.awaitingDate, awaiting: true } };
  }
  // A escolha já é o dia mais barato: a frase diz isso, sem economia a sugerir
  // (#5693) — "é a ida mais econômica" sobre a data escolhida lia como conselho.
  if (out === bestDate) return { day: copy.chosenCheapest };
  const follow = savingsFollow(heat, out, bestDate, copy, locale);
  return follow ? { day, follow } : { day };
}

/** A 2ª linha da economia de trocar `out` por `bestDate`; nada quando não há o
 *  que economizar (ver `legSavings`) ou moeda para dizer quanto. */
function savingsFollow(
  heat: PlannerHeatView,
  out: string,
  bestDate: string,
  copy: FlightPlannerCopy,
  locale: string,
): SkirtBest['follow'] {
  const amount = heat.outbound ? legSavings(heat.outbound.days, out, bestDate) : undefined;
  if (amount === undefined || !heat.currency) return undefined;
  // Whole units: the estimate reads "R$ 150", never "R$ 150,00".
  const value = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: heat.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
  return {
    text: copy.savesLine({ amount: value }),
    inline: copy.savesLineInline({ amount: value }),
    awaiting: false,
  };
}
